<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'templates',
    path: '/templates',
    component: 'Templates',
    params: [
      {
        name: 'id',
        prop: 'resourceId',
      },
    ],
    icon: 'fa-file-invoice',
    title: () => pgettext('Templates', 'Templates'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Templates',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    resourceId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const clearTemplate = (...args) => {
    item.value = null;
    editor.value = null;
  };

  const formId = ref(null);
  const formName = ref('template');
  const item = ref(null);
  const editor = ref(null);
  const contextData = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    editor: editor.value,
    contextData: contextData.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return props.resourceId
      ? $fn.value.fetch('read_template', {
          asResource: true,
          parameters: { id: props.resourceId },
        })
      : clearTemplate();
  };
  const $$load_editor = () => {
    return item.value ? item.value.editor() : null;
  };
  const $$load_contextData = () => {
    return { sampleData: null };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'editor', dependsOn: ['item'] },
      {
        name: 'contextData',
        cache: { type: 'local', key: 'Template.sampleData' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      editor: $$load_editor,
      contextData: $$load_contextData,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      item,
      editor,
      contextData,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const sampleData = computed(() => {
    if (loaded.value) {
      return contextData.value.sampleData;
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Templates', 'Templates') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.resourceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_gtsorhbxa_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_4615685607825208_setData_0ukwkcqdk_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contextData',
      value: { sampleData: null },
      resolve: false,
      noTouchLastLoaded: true,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_navigate_ecv2gjkr5_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.templates.template',
        params: {
          id: $event.data.option.id,
        },
        replace: true,
      },
    };
  };
  const act_8823104369878821_showModal_2psohzx4n_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'TemplatePreview',
      props: {
        item: item.value,
        sampleData: contextData.value.sampleData,
      },
    };
  };
  const act_2237382557075493_showModal_lz0owlxud_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('read_template', { id: 'import' }),
        accept: '.yaml,.yml,.json',
      },
    };
  };
  const act_1040167445267876_setData_m02dunt91_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1040167445267876_success_p709damt0_getWhen = async (
    $event
  ) => {
    return $event.data.response.data;
  };
  const act_1040167445267876_rest_hbj7hil1x_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: {
        self: item.value.links.parent,
        object: 'list',
        data: [],
      },
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_gtsorhbxa =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_gtsorhbxa_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hy8ke93yq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_gtsorhbxa,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hy8ke93yq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_yqppn7krm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_pahm8ggqw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_yqppn7krm,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_pahm8ggqw],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_wf4ogi2zm =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_83s4sx2oy =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_wf4ogi2zm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_83s4sx2oy],
    event: 'close',
  });
  const act_4615685607825208_setData_0ukwkcqdk = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_0ukwkcqdk_getActionArgs,
  });
  const inst_evh_4615685607825208_optionSelected_p2vq1w6il =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_0ukwkcqdk,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_4615685607825208_navigate_ecv2gjkr5 =
    new actionTypes.NavigateAction({
      actionArgs: act_4615685607825208_navigate_ecv2gjkr5_getActionArgs,
    });
  const inst_evh_4615685607825208_optionSelected_utqvmv7sr =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_navigate_ecv2gjkr5,
      event: 'optionSelected',
      displayName: 'navigate',
    });
  const inst_evh_4615685607825208_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4615685607825208_optionSelected_p2vq1w6il,
        inst_evh_4615685607825208_optionSelected_utqvmv7sr,
      ],
      event: 'optionSelected',
    });
  const act_2095089031469157_reloadComponentData_i1vy22qk2 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_2095089031469157_clickPreventStop_leoics46e =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_i1vy22qk2,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_leoics46e],
      event: 'click.prevent.stop',
    });
  const act_8823104369878821_showModal_2psohzx4n =
    new actionTypes.ShowModalAction({
      actionArgs: act_8823104369878821_showModal_2psohzx4n_getActionArgs,
    });
  const inst_evh_8823104369878821_clickPreventStop_3z5y9sli6 =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_showModal_2psohzx4n,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8823104369878821_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8823104369878821_clickPreventStop_3z5y9sli6],
      event: 'click.prevent.stop',
    });
  const act_2237382557075493_reloadComponentData_cdeh8rxrk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2237382557075493_close_55sfo169c =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_reloadComponentData_cdeh8rxrk,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_2237382557075493_showModal_lz0owlxud =
    new actionTypes.ShowModalAction({
      actionArgs: act_2237382557075493_showModal_lz0owlxud_getActionArgs,
      events: [inst_evh_2237382557075493_close_55sfo169c],
    });
  const inst_evh_2237382557075493_clickPreventStop_t79t9bo2q =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_showModal_lz0owlxud,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2237382557075493_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2237382557075493_clickPreventStop_t79t9bo2q],
      event: 'click.prevent.stop',
    });
  const act_1040167445267876_setData_m02dunt91 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_m02dunt91_getActionArgs,
  });
  const inst_evh_1040167445267876_success_p709damt0 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_m02dunt91,
      event: 'success',
      when: inst_evh_1040167445267876_success_p709damt0_getWhen,
      displayName: 'setData',
    });
  const act_1040167445267876_rest_hbj7hil1x = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_hbj7hil1x_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_p709damt0],
  });
  const inst_evh_1040167445267876_submit_2o9cs46j3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_hbj7hil1x,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_2o9cs46j3],
    event: 'submit',
  });

  const actions = {
    inst_evh_7315092382398562_close_hy8ke93yq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_pahm8ggqw,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_83s4sx2oy,
    inst_evh_8564662037462133_close,
    inst_evh_4615685607825208_optionSelected_p2vq1w6il,
    inst_evh_4615685607825208_optionSelected_utqvmv7sr,
    inst_evh_4615685607825208_optionSelected,
    inst_evh_2095089031469157_clickPreventStop_leoics46e,
    reload: inst_evh_2095089031469157_clickPreventStop_leoics46e,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_8823104369878821_clickPreventStop_3z5y9sli6,
    inst_evh_8823104369878821_clickPreventStop,
    inst_evh_2237382557075493_close_55sfo169c,
    inst_evh_2237382557075493_clickPreventStop_t79t9bo2q,
    inst_evh_2237382557075493_clickPreventStop,
    inst_evh_1040167445267876_success_p709damt0,
    inst_evh_1040167445267876_submit_2o9cs46j3,
    save: inst_evh_1040167445267876_submit_2o9cs46j3,
    inst_evh_1040167445267876_submit,
  };

  const evh_7315092382398562_close_hy8ke93yq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hy8ke93yq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_pahm8ggqw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_pahm8ggqw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_83s4sx2oy = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_83s4sx2oy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected_p2vq1w6il = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_optionSelected_p2vq1w6il.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected_utqvmv7sr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_optionSelected_utqvmv7sr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_optionSelected = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_leoics46e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_leoics46e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_clickPreventStop_3z5y9sli6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8823104369878821_clickPreventStop_3z5y9sli6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_close_55sfo169c = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_close_55sfo169c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_clickPreventStop_t79t9bo2q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2237382557075493_clickPreventStop_t79t9bo2q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_p709damt0 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_p709damt0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_2o9cs46j3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_2o9cs46j3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Templates"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <ItpBox :gap="2">
          <UcTemplateSelector
            :resourceId="resourceId"
            :reload="lastLoaded"
            @optionSelected="evh_4615685607825208_optionSelected($event, {})"
          >
          </UcTemplateSelector>
          <ItpButton
            variant="default"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <Vr class="mx-2"> </Vr>
          <ItpButton
            :form="formId"
            :text="pgettext('Templates', 'Save')"
            :icon="$config.saveIcon"
            type="submit"
            variant="primary"
            default
            :disabled="!item"
            :spinning="actions.save.isRunning"
          >
          </ItpButton>
          <ItpButton
            :icon="$config.previewIcon"
            :text="pgettext('Templates', 'Preview')"
            variant="light"
            :disabled="!item || !sampleData"
            @click.prevent.stop="
              evh_8823104369878821_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            :icon="$config.exportIcon"
            :text="pgettext('Templates', 'Export')"
            variant="light"
            :disabled="!item"
            use-auth
            force-download
            downloadFileName="templates.yaml"
            :href="$fn.urlFor('read_template', { id: 'export' })"
          >
          </ItpButton>
          <ItpButton
            :icon="$config.importIcon"
            :text="pgettext('Templates', 'Import')"
            variant="light"
            @click.prevent.stop="
              evh_2237382557075493_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <UcTemplate :item="editor" :context="contextData" v-if="editor">
        </UcTemplate>
      </ItpForm>
    </UcBody>
    <router-view class="d-none"> </router-view>
  </ItpPage>
</template>
